import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterFormsService } from '../../services/supportCenterForms'
import type {
  SupportCenterForm,
  SupportCenterFormInput,
} from '../../types/supportCenterForms'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  MODULE_NAME,
  getErrorMessage,
} from '../../components/centralAmparo/parametrosHelpers'
import ParametrosDeleteModal from './ParametrosDeleteModal'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

const ParametrosFormulariosTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<SupportCenterForm[]>([])
  const [filter, setFilter] = React.useState('')

  const [formModal, setFormModal] = React.useState<SupportCenterFormInput | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await supportCenterFormsService.list()
      setRows(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredRows = rows.filter((row) => {
    const term = filter.trim().toLocaleLowerCase('pt-BR')
    if (!term) return true
    return (
      row.name.toLocaleLowerCase('pt-BR').includes(term) ||
      row.form_key.toLocaleLowerCase('pt-BR').includes(term) ||
      row.table_name.toLocaleLowerCase('pt-BR').includes(term) ||
      row.triage_table_name.toLocaleLowerCase('pt-BR').includes(term)
    )
  })

  const openNew = () => {
    setFormError(null)
    setFormModal({ name: '', form_key: '', table_name: '', triage_table_name: '' })
  }

  const openEdit = (row: SupportCenterForm) => {
    setFormError(null)
    setFormModal({
      original_form_key: row.form_key,
      name: row.name,
      form_key: row.form_key,
      table_name: row.table_name,
      triage_table_name: row.triage_table_name,
    })
  }

  const closeForm = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleSave = async () => {
    if (!formModal || !user?.id) return
    if (!formModal.name.trim()) {
      setFormError('Informe o nome do formulário.')
      return
    }
    if (!formModal.form_key.trim()) {
      setFormError('Informe a key do formulário.')
      return
    }
    if (!formModal.table_name.trim()) {
      setFormError('Informe a tabela de atendimento.')
      return
    }
    if (!formModal.triage_table_name.trim()) {
      setFormError('Informe a tabela de triagem.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      const savedKey = await supportCenterFormsService.upsert({
        ...formModal,
        name: formModal.name.trim(),
        form_key: formModal.form_key.trim(),
        table_name: formModal.table_name.trim(),
        triage_table_name: formModal.triage_table_name.trim(),
      })
      if (formModal.original_form_key) {
        void logUpdate(
          'support_center_form',
          savedKey,
          `Atualizou formulário: ${formModal.name.trim()} (${savedKey})`
        )
      } else {
        void logCreate(
          'support_center_form',
          savedKey,
          `Criou formulário: ${formModal.name.trim()} (${savedKey})`
        )
      }
      closeForm()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const handleDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterFormsService.softDelete(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete(
        'support_center_form',
        deleteModal.id,
        `Excluiu formulário: ${deleteModal.label} — ${deleteReason.trim()}`
      )
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Formulários
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredRows.length}
              {filter.trim() && filteredRows.length !== rows.length
                ? ` de ${rows.length}`
                : ''}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por nome, key ou tabela..."
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
            {canInsert && (
              <Button
                type="button"
                variant="solid"
                onClick={openNew}
                className="inline-flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Novo formulário
              </Button>
            )}
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Text size="sm" color="muted">
              {filter.trim()
                ? 'Nenhum formulário encontrado com o filtro informado.'
                : 'Nenhum formulário cadastrado.'}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tabela atendimento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tabela de triagem
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRows.map((item) => (
                  <tr
                    key={item.form_key}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        {item.form_key}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 font-mono">
                      {item.table_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 font-mono">
                      {item.triage_table_name || '—'}
                    </td>
                    {(canUpdate || canDelete) && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => openEdit(item)}
                              className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => {
                                setDeleteModal({
                                  id: item.form_key,
                                  label: `${item.name} (${item.form_key})`,
                                })
                                setDeleteReason('')
                              }}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formModal &&
        createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {formModal.original_form_key ? 'Editar Formulário' : 'Novo Formulário'}
              </Text>
              <Button variant="ghost" onClick={closeForm} disabled={submittingForm}>
                Fechar
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.name}
                  onChange={(e) => setFormModal({ ...formModal, name: e.target.value })}
                  placeholder="Ex: Psicológico"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.form_key}
                  onChange={(e) => setFormModal({ ...formModal, form_key: e.target.value })}
                  placeholder="Ex: psicologico"
                  className={inputClass}
                />
                <Text size="xs" color="muted" className="mt-1 block">
                  Letras minúsculas, números e _. Espaços são removidos.
                </Text>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tabela atendimento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.table_name}
                  onChange={(e) => setFormModal({ ...formModal, table_name: e.target.value })}
                  placeholder="Ex: support_center_attendance_psychological"
                  className={inputClass}
                />
                <Text size="xs" color="muted" className="mt-1 block">
                  Nome da tabela no banco para o formulário de atendimento.
                </Text>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tabela de triagem <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.triage_table_name}
                  onChange={(e) =>
                    setFormModal({ ...formModal, triage_table_name: e.target.value })
                  }
                  placeholder="Ex: support_center_triage_psychological"
                  className={inputClass}
                />
                <Text size="xs" color="muted" className="mt-1 block">
                  Nome da tabela no banco para o detalhe específico da triagem.
                </Text>
              </div>
            </div>
            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeForm} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSave()} disabled={submittingForm}>
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={closeDeleteModal}
          onConfirm={() => void handleDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosFormulariosTab
